
import { EventBus } from '@/plugins/eventBus'
import { PROGRESSION_ACTIONS } from '@/constants/progressionActions'
import { trackUserProgress } from '@/utils/userProgress'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { Bar } from 'vue-chartjs'
import Vue, { PropType } from 'vue'
import { state } from '@/plugins/state'
import FilesMixin from '@/mixins/FilesMixin.vue'
import { VueConstructor } from 'vue/types/umd'
import {
  accountStatisticsQueryFromState,
  accountStatisticsStatesEqual,
  accountStatisticsUrlStateFromDate,
  dateFromAccountStatisticsState,
  isoWeekNumberFromDate,
  isoWeekYearFromDate,
  parseAccountStatisticsQuery,
  routeQueriesEqual,
  routeQueryToStringRecord,
} from '@/utils/listParagonRouteQuery'
import type { RouteQuery } from '@/utils/listParagonRouteQuery'

// Create a new chart component
const BarChart = Vue.extend({
  extends: Bar as any,
  props: {
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      required: true
    }
  },
  watch: {
    chartData: {
      handler(oldValue, newValue) {
        // If the first label is different, destroy the old chart and render a new one
        if (oldValue.labels[0] != newValue.labels[0]) {
          if ((this as any)._chart) {
            (this as any)._chart.destroy()
          }
          // Render new chart
          this.$nextTick(() => {
            (this as any).renderChart(this.chartData, this.options)
          })
        }
      },
      deep: true
    }
  },
  mounted() {
    (this as any).renderChart(this.chartData, this.options)
  }
})

interface Space {
  id: number,
  name: string
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof FilesMixin>>).extend({
  name: 'SpaceStatistics',
  components: {
    BarChart
  },
  mixins: [
  FilesMixin
  ],
  props: {
    /**
    * Statistics to display
    */
    statsToDisplay: {
      type: Array as PropType<string[]>,
      default: () => [
      'standard_subscription',
      'kiosk_subscription',
      'express_subscription',
      'active_proposition',
      'active_worker',
      'associated_worker',
      'worker_user',
      'order',
      'order_fully_honored',
      'order_partially_honored',
      'order_non_honored',
      'order_quantity',
      'sms_job_proposition',
      'accepted_job_proposition',
      'order_job_taken',
      'dpae_confirmation',
      'availability_request',
      'non_interpreted_answer',
      'availability_request_qualification',
      'availability_request_link_qualification',
      'message_sent',
      'message_received',
      'ocr',
      'availability_history',
      'worker_in_contract',
      'applicant',
      'applicant_jobboard',
      'application',
      'application_jobboard'
      ]
    }
  },
  data: () => ({
    loading: true, // Loading state
    spaces: [] as Array<Space>, // All spaces
    selectedSpaces: [] as Array<Space>, // Selected spaces
    scope: 'week', // Scope of the statistics
    currentDate: new Date(), // Current date
    isAdmin: globalThis.location.href.includes('/admin'), // Check if we are on the admin page
    statsCache: new Map(), // Cache of statistics
    cacheSelectedSpaces: [] as Array<Space>, // Cache of selected spaces
    cacheVersion: 0, // Version number to trigger reactivity when cache is updated
    syncingStatisticsFromRoute: false,
    syncingStatisticsToRoute: false,
    lastWeekDate: null as Date | null,
    lastMonthDate: null as Date | null,
  }),
  methods: {
    /**
    * Previous date
    */
    previous() {
      const newDate = new Date(this.currentDate)

      if (this.scope === 'year') {
        newDate.setFullYear(newDate.getFullYear() - 1)
      } else if (this.scope === 'month') {
        const beforeMonth = newDate.getMonth()
        newDate.setMonth(newDate.getMonth() - 1)
        // when we came from a month with 31 days, removing one month
        // date usually doesn't exists and javascript Date object is
        // setting it to the closest one in the future so the first day
        // of the same month (which we doesn't want) so we compensate
        // by removing one day from the first of the same month
        if (newDate.getMonth() === beforeMonth) {
          newDate.setDate(newDate.getDate() - 1)
        }
      } else { // week
        newDate.setDate(newDate.getDate() - 7)
      }

      this.currentDate = newDate
      this.getStatistics()
      this.syncAccountStatisticsUrl()
    },
    /**
    * Next date
    */
    next() {
      const newDate = new Date(this.currentDate)
      
      if (this.scope === 'year') {
        newDate.setFullYear(newDate.getFullYear() + 1)
      } else if (this.scope === 'month') {
        newDate.setMonth(newDate.getMonth() + 1)
      } else { // week
        newDate.setDate(newDate.getDate() + 7)
      }
      
      this.currentDate = newDate
      this.getStatistics()
      this.syncAccountStatisticsUrl()
    },
    /**
    * Today
    */
    today() {
      this.currentDate = new Date()
      this.getStatistics()
      this.syncAccountStatisticsUrl()
    },
    /**
    * Helper method to get ISO week number
    */
    getWeekNumber(date: Date): number {
      return isoWeekNumberFromDate(date)
    },
    /**
    * Get ISO week year (may differ from calendar year)
    */
    getWeekYear(date: Date): number {
      return isoWeekYearFromDate(date)
    },
    currentAccountStatisticsState() {
      return accountStatisticsUrlStateFromDate(
        this.scope as 'year' | 'month' | 'week',
        this.currentDate,
        this.selectedSpaces.map((space: Space) => space.id),
      )
    },
    /**
    * Download XLSX file of selected spaces
    */
    async getXLSX() {
      if (this.selectedSpaces.length == 0) {
        EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.statistics.please_select_one_space'), color: 'error' })
        return
      }
      
      this.loading = true
      let url = (this.isAdmin ? '/v1/admin/spaces/statistics' : '/v1/space/statistics') + '?xlsx=true&year=' + this.selectedYear
      + (this.scope === 'month' ? '&month=' + this.selectedMonth : '')
      + (this.scope === 'week' ? '&week=' + this.selectedWeek : '')
      + '&stats=' + this.statsToDisplay.join(',')
      + '&id=' + this.selectedSpaces.map((space: Space) => space.id).join(',')
      
      await this.launchFileDownload(url)
      if (this.scope === 'week') {
        await trackUserProgress(PROGRESSION_ACTIONS.PILOTAGE_REPORT_EXPORTED, {
          year: this.selectedYear,
          week: this.selectedWeek,
        })
      }
      
      this.loading = false
    },
    /**
    * Select or unselect all spaces
    */
    selectAllSpaces() {
      if (this.selectedSpaces.length == this.spaces.length) {
        this.selectedSpaces = []
      } else {
        this.selectedSpaces = this.spaces
      }
      this.syncAccountStatisticsUrl()
    },
    onSelectedSpacesBlur(): void {
      this.cacheSelectedSpaces = this.selectedSpaces
      this.getStatistics()
      this.syncAccountStatisticsUrl()
    },
    applyAccountStatisticsFromRoute(): void {
      if (!this.shouldSyncAccountStatisticsUrl || !this.$route) return
      const parsed = parseAccountStatisticsQuery(this.$route.query as RouteQuery)
      if (!parsed) return
      if (accountStatisticsStatesEqual(parsed, this.currentAccountStatisticsState())) return

      this.syncingStatisticsFromRoute = true
      this.scope = parsed.scope
      this.currentDate = dateFromAccountStatisticsState(parsed)

      if (parsed.spaceIds.length > 0 && this.spaces.length > 0) {
        const selected = this.spaces.filter((space) => parsed.spaceIds.includes(space.id))
        if (selected.length > 0) {
          this.selectedSpaces = selected
        }
      }

      if (parsed.scope === 'week') {
        this.lastWeekDate = new Date(this.currentDate)
      } else if (parsed.scope === 'month') {
        this.lastMonthDate = new Date(this.currentDate)
      }

      this.cacheSelectedSpaces = this.selectedSpaces
      this.$nextTick(() => {
        this.syncingStatisticsFromRoute = false
        if (this.selectedSpaces.length > 0) {
          this.getStatistics()
        }
      })
    },
    syncAccountStatisticsUrl(): void {
      if (!this.shouldSyncAccountStatisticsUrl || this.syncingStatisticsFromRoute || !this.$router || !this.$route) {
        return
      }
      if (this.$route.path !== '/account/statistics') return

      const query = accountStatisticsQueryFromState(this.currentAccountStatisticsState())
      const currentQuery = routeQueryToStringRecord(this.$route.query as RouteQuery)
      if (routeQueriesEqual(query, currentQuery)) return

      this.syncingStatisticsToRoute = true
      this.$router.replace({ path: '/account/statistics', query }).catch(() => undefined).finally(() => {
        this.$nextTick(() => {
          this.syncingStatisticsToRoute = false
        })
      })
    },
    /**
    * Fetch spaces statistics and redraw charts
    */
    getStatistics() {
      // Check if no space is selected
      if (this.selectedSpaces.length == 0) {
        EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.statistics.please_select_one_space'), color: 'error' })
        return
      }
      
      // Check if we can use cached data
      if (this.statsCache.has(this.cacheKey)) {
        return
      }
      
      // Fetch statistics
      this.loading = true
      // Store the cache key at request time to ensure we cache with the correct key
      const requestCacheKey = this.cacheKey
      let url = (this.isAdmin ? '/v1/admin/spaces/statistics' : '/v1/space/statistics') + '?year=' + this.selectedYear
      + (this.scope === 'month' ? '&month=' + this.selectedMonth : '')
      + (this.scope === 'week' ? '&week=' + this.selectedWeek : '')
      + '&stats=' + this.statsToDisplay.join(',')
      + '&id=' + this.selectedSpaces.map((space: Space) => space.id).join(',')
      
      axios.get(url)
      .then((response: AxiosResponse) => {
        // Only cache if the cache key hasn't changed during the request
        // This prevents caching stale data if user changed parameters while request was pending
        if (this.cacheKey === requestCacheKey) {
          // Cache all results using the key from when the request was made
          this.statsCache.set(requestCacheKey, response.data)
          // Increment cacheVersion to trigger reactivity in cachedStats computed property
          this.cacheVersion++
        }
        // If cache key changed, the response is stale and we ignore it
        // A new request with the updated key should already be in progress or will be triggered
      })
      .catch((error: AxiosError) => {
        EventBus.$emit('snackbar', { axiosError: error })
      })
      .finally(() => {
        this.loading = false
      })
    }
  },
  watch: {
    scope(newScope: string, oldScope: string) {
      if (oldScope === 'week') {
        this.lastWeekDate = new Date(this.currentDate)
      } else if (oldScope === 'month') {
        this.lastMonthDate = new Date(this.currentDate)
      }
      if (newScope === 'week' && this.lastWeekDate) {
        this.currentDate = new Date(this.lastWeekDate)
      } else if (newScope === 'month' && this.lastMonthDate) {
        this.currentDate = new Date(this.lastMonthDate)
      }
      this.getStatistics()
      this.syncAccountStatisticsUrl()
    },
    '$route.query': {
      handler() {
        if (this.syncingStatisticsToRoute) return
        this.applyAccountStatisticsFromRoute()
      },
      deep: true,
    },
    selectedSpaces: {
      handler() {
        this.syncAccountStatisticsUrl()
      },
      deep: true,
    },
  },
  mounted() {
    this.loading = true
    
    // retrieve spaces
    axios.get(this.isAdmin ? '/v1/admin/spaces?per_page=0' : '/v1/spaces?deleted=false&per_page=0')
      .then((response: AxiosResponse) => {
        // store retrieved spaces
        this.spaces = response.data
        const selectedSpace = state.agency
          ? this.spaces.find(space => space.id === Number(state.agency))
          : undefined
        if (selectedSpace) {
          this.selectedSpaces = [selectedSpace] // select the space of the current agency if it exists
        } else {
          this.selectedSpaces = this.spaces // select all spaces by default
        }
        // Set cacheSelectedSpaces before calling today() to ensure cache key matches
        this.cacheSelectedSpaces = this.selectedSpaces
        const parsed = this.shouldSyncAccountStatisticsUrl
          ? parseAccountStatisticsQuery(this.$route.query as RouteQuery)
          : null
        if (parsed) {
          this.applyAccountStatisticsFromRoute()
        } else {
          this.today()
        }
        this.syncAccountStatisticsUrl()
      })
      .catch((error: AxiosError) => {
        EventBus.$emit('snackbar', { axiosError: error })
      })
      .finally(() => {
        this.loading = false
      })
    },
    computed: {
      shouldSyncAccountStatisticsUrl(): boolean {
        return !this.isAdmin && this.$route?.path === '/account/statistics'
      },
      /**
      * Return the current year
      * For week scope, returns ISO week year (may differ from calendar year)
      */
      selectedYear(): number {
        if (this.scope === 'week') {
          return this.getWeekYear(this.currentDate)
        }
        return this.currentDate.getFullYear()
      },
      /**
      * Return the current month
      */
      selectedMonth(): number {
        return this.currentDate.getMonth() + 1
      },
      /**
      * Return the current week number
      */
      selectedWeek(): number {
        return this.getWeekNumber(this.currentDate)
      },
      /**
      * Generate cache key for current request
      */
      cacheKey(): string {
        return `${this.scope}_${this.selectedYear}_${this.scope === 'month' ? this.selectedMonth : ''}${this.scope === 'week' ? this.selectedWeek : ''}_${this.cacheSelectedSpaces.map(s => s.id).sort().join(',')}`
      },
      /**
      * Get cached statistics for current cache key, returns empty array if not cached yet
      * Uses cacheVersion to ensure reactivity when cache is updated
      */
      cachedStats(): Array<any> {
        // Access cacheVersion to ensure this computed property is reactive
        // The cacheVersion dependency ensures Vue tracks changes to the Map
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.cacheVersion
        return this.statsCache.get(this.cacheKey) || []
      },
      /**
      * Check if today is present
      */
      isTodayPresent(): boolean {
        if (this.scope === 'year') {
          return this.selectedYear === new Date().getFullYear()
        } else if (this.scope === 'month') {
          return this.selectedYear === new Date().getFullYear() && this.selectedMonth === new Date().getMonth() + 1
        } else { // week
          return this.selectedYear === new Date().getFullYear() && this.selectedWeek === this.getWeekNumber(new Date())
        }
      },
      /**
      * Return icon used on select all spaces v-autocomplete
      */
      selectAllSpacesIcon(): string {
        if (this.selectedSpaces.length == 0) {
          return 'mdi-checkbox-blank-outline'
        } else if (this.selectedSpaces.length == this.spaces.length) {
          return 'mdi-close-box'
        } else {
          return 'mdi-minus-box'
        }
      },
      chartOptions() {
        return {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true
              }
            }]
          },
          legend: {
            display: true,
            labels: {
              fontColor: this.$vuetify.theme.currentTheme.primary
            }
          }
        }
      }
    }
  })
